import { useEffect, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { IMapLocation } from "../../../../shared/oversight-core/interfaces/map-location";
import GoogleMapWithoutModal from "../../../../shared/oversight-core/shared-components/google-map-without-modal/google-map-without-modal";
import ModalContainer, {
  ModelContainerProps,
} from "../../../../shared/oversight-core/ui-elements/modal-container/modal-container";
import styles from "./submit-for-expert-approval-modal.module.scss";

interface IFormInput {
  note: string;
}

const defaultFormValues: IFormInput = {
  note: "",
};

interface IProps extends Omit<ModelContainerProps, "onConfirm"> {
  onConfirm: (
    address: string,
    longitude: number,
    latitude: number,
    note: string
  ) => void;
  includeOtherFees: boolean;
}

const defaultLocationDetails: IMapLocation = {
  name: "",
  lng: 37.906193,
  lat: -0.023559,
};

const SubmitForExpertApprovalModal = (props: IProps) => {
  const { show, includeOtherFees, onCancel, onClose, onConfirm } = props;
  const [selectedLocationDetails, setSelectedLocationDetails] =
    useState<IMapLocation>({ ...defaultLocationDetails });
  const [locationError, setLocationError] = useState("");
  const [isShowingError, setIsShowingError] = useState(false);

  const { register, handleSubmit, reset } = useForm<IFormInput>({
    defaultValues: { ...defaultFormValues },
  });

  const onSubmit = (data: IFormInput) => {
    if (!selectedLocationDetails.name && includeOtherFees) {
      setLocationError("Please select your installation site");
      setIsShowingError(true);
      return;
    }

    setLocationError("");
    onConfirm(
      selectedLocationDetails.name,
      selectedLocationDetails.lng,
      selectedLocationDetails.lat,
      data.note
    );
  };

  useEffect(() => {
    if (!show) {
      reset();
    }
  }, [show]);

  useEffect(() => {
    if (isShowingError && selectedLocationDetails.name) {
      setLocationError("");
    }
  }, [isShowingError, selectedLocationDetails.name]);

  return (
    <ModalContainer
      show={show}
      title="Submit for Expert Approval"
      onCancel={onCancel}
      onClose={onClose}
      onConfirm={handleSubmit(onSubmit)}
    >
      <>
        {includeOtherFees && (
          <Row>
            <Col>
              <Row>
                <Col className="text-label font-weight-500 font-size-14">
                  Select your installation site from the map
                </Col>
              </Row>
              <Row>
                <Col>
                  <div>
                    <GoogleMapWithoutModal
                      setSelectedLocationDetails={setSelectedLocationDetails}
                      selectedLocationDetails={selectedLocationDetails}
                    />
                  </div>
                </Col>
              </Row>
              {locationError && (
                <Row>
                  <Col>
                    <span className="text-danger">{locationError}</span>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>
        )}
        <Row className="mt-3">
          <Col>
            <Row>
              <Col className="text-label font-weight-500 font-size-14">
                Note (any additional information for the expert)
              </Col>
            </Row>
            <Row>
              <Col>
                <textarea
                  {...register("note", {
                    required: false,
                  })}
                  name="note"
                  placeholder="Note"
                  className={`${styles.textArea} w-100`}
                />
              </Col>
            </Row>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col className="font-weight-400 font-size-14 text-color-2">
            Once this estimation is submitted, it cannot be edited until it is
            reviewed by an administrator. If you need to make changes later, you
            can duplicate this estimation and submit a new one.
          </Col>
        </Row>
      </>
    </ModalContainer>
  );
};

export default SubmitForExpertApprovalModal;
